/**
 * Track Settings Tab — הגדרות ואישיות ה-AI
 *
 * Where the advisor steers an area without a deploy: the AI's persona, the copy
 * shown to visitors, whether the area is open, and its AI spend cap.
 *
 * The persona is the CACHED system prompt. Two consequences the editor states
 * out loud, because both cost money and neither is visible from the text:
 *   • saving it resets the prompt cache once (the next message costs a bit more);
 *   • a persona under ~1024 tokens is not cached AT ALL, so every message pays
 *     full price. The server returns that warning; we surface it rather than
 *     silently accepting a stub.
 */

import { useEffect, useState } from 'react'
import { AlertCircle, CheckCircle, Info, RotateCcw, Save } from 'lucide-react'
import { useAdminStore } from '../../store/adminStore'
import { useAuthStore } from '../../store/authStore'
import type { TrackKey } from '../../config/tracks'
import type { TrackAdmin } from '../../types'

interface TrackSettingsTabProps {
  track: TrackKey
}

export default function TrackSettingsTab({ track }: TrackSettingsTabProps) {
  // Availability and cost belong to the owner of the app, not to whoever runs the
  // area's content. A section admin (sub_admin) edits the copy and the persona.
  const isOwner = useAuthStore((state) => state.user?.role) === 'admin'

  const {
    portalTracks,
    portalTracksLoading,
    portalTracksError,
    fetchPortalTracks,
    updatePortalTrack,
    restoreTrackPersona,
  } = useAdminStore()

  const [draft, setDraft] = useState<Partial<TrackAdmin>>({})
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState<string | null>(null)
  const [warning, setWarning] = useState<string | null>(null)

  const record = portalTracks.find((t) => t.key === track)

  useEffect(() => {
    if (portalTracks.length === 0) void fetchPortalTracks()
  }, [fetchPortalTracks, portalTracks.length])

  useEffect(() => {
    if (record) {
      setDraft({
        name: record.name,
        shortName: record.shortName,
        tagline: record.tagline,
        heroText: record.heroText,
        guideName: record.guideName,
        chatIntro: record.chatIntro,
        personaPrompt: record.personaPrompt,
        isActive: record.isActive,
        isPublic: record.isPublic,
        spendCapUSD: record.spendCapUSD,
      })
    }
  }, [record])

  const set = <K extends keyof TrackAdmin>(key: K, value: TrackAdmin[K]) => {
    setDraft((d) => ({ ...d, [key]: value }))
    setSaved(null)
  }

  const handleSave = async () => {
    setSaving(true)
    setSaved(null)
    setWarning(null)
    try {
      const w = await updatePortalTrack(track, draft)
      setWarning(w || null)
      setSaved('ההגדרות נשמרו')
    } catch {
      // The store surfaces the message through portalTracksError.
    } finally {
      setSaving(false)
    }
  }

  const handleRestore = async () => {
    if (
      !window.confirm(
        'לשחזר את אישיות ברירת המחדל?\n\nכל שינוי שביצעת בטקסט האישיות יידרס.'
      )
    ) {
      return
    }
    await restoreTrackPersona(track)
    setSaved('אישיות ברירת המחדל שוחזרה')
  }

  if (portalTracksLoading && !record) {
    return <p className="text-sm text-gray-600">טוען הגדרות...</p>
  }

  if (!record) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-amber-50 p-4 text-sm text-amber-900">
        <AlertCircle size={18} aria-hidden="true" />
        המסלול עדיין לא קיים במסד הנתונים. יש להריץ <code className="mx-1">npm run migrate:tracks</code>
        בשרת.
      </div>
    )
  }

  const personaTokens = record.personaTokensEstimate ?? 0
  const minTokens = record.personaCacheMinTokens ?? 1024

  return (
    <div className="max-w-3xl space-y-6">
      {portalTracksError && (
        <div
          className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800"
          role="alert"
        >
          <AlertCircle size={16} aria-hidden="true" />
          {portalTracksError}
        </div>
      )}

      {saved && (
        <div
          className="flex items-center gap-2 rounded-lg bg-green-50 p-3 text-sm text-green-800"
          role="status"
        >
          <CheckCircle size={16} aria-hidden="true" />
          {saved}
        </div>
      )}

      {warning && (
        <div
          className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900"
          role="alert"
        >
          <AlertCircle size={16} aria-hidden="true" className="mt-0.5 flex-shrink-0" />
          {warning}
        </div>
      )}

      {/* Switches — owner-only: each of these either takes the area offline or
          moves the AI bill, so a section admin (sub_admin) never sees them. The
          server strips the same fields from their save. */}
      {isOwner && (
      <section className="card">
        <h3 className="card-header">זמינות</h3>
        <div className="space-y-3">
          <label className="flex items-start gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              className="mt-1"
              checked={!!draft.isActive}
              onChange={(e) => set('isActive', e.target.checked)}
            />
            <span>
              <strong>האזור פעיל</strong>
              <span className="block text-xs text-gray-600">
                כיבוי מסתיר את האזור לחלוטין — כתובת ה-URL תחזיר "לא נמצא". זהו מתג
                החירום אם ה-AI מתנהג לא כשורה.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              className="mt-1"
              checked={!!draft.isPublic}
              onChange={(e) => set('isPublic', e.target.checked)}
              disabled={record.requiresClassCode}
            />
            <span>
              <strong>הרשמה פתוחה לציבור</strong>
              <span className="block text-xs text-gray-600">
                {record.requiresClassCode
                  ? 'אזור זה נדרש קוד כיתה ולכן אינו פתוח להרשמה חופשית.'
                  : 'כיבוי משאיר את האזור פעיל למי שכבר נרשם, אבל עוצר הרשמות חדשות.'}
              </span>
            </span>
          </label>
        </div>
      </section>
      )}

      {/* Copy */}
      <section className="card">
        <h3 className="card-header">טקסטים שהמשתמש רואה</h3>
        <div className="space-y-4">
          <div>
            <label htmlFor="track-name" className="mb-1 block text-sm font-medium text-gray-700">
              שם האזור
            </label>
            <input
              id="track-name"
              className="input"
              value={draft.name || ''}
              onChange={(e) => set('name', e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="track-tagline" className="mb-1 block text-sm font-medium text-gray-700">
              משפט מפתח
            </label>
            <input
              id="track-tagline"
              className="input"
              value={draft.tagline || ''}
              onChange={(e) => set('tagline', e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="track-hero" className="mb-1 block text-sm font-medium text-gray-700">
              תיאור בעמוד הכניסה
            </label>
            <textarea
              id="track-hero"
              className="input min-h-[80px]"
              value={draft.heroText || ''}
              onChange={(e) => set('heroText', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="track-guide" className="mb-1 block text-sm font-medium text-gray-700">
                שם היועץ בצ'אט
              </label>
              <input
                id="track-guide"
                className="input"
                value={draft.guideName || ''}
                onChange={(e) => set('guideName', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="track-intro" className="mb-1 block text-sm font-medium text-gray-700">
                משפט פתיחה בצ'אט
              </label>
              <input
                id="track-intro"
                className="input"
                value={draft.chatIntro || ''}
                onChange={(e) => set('chatIntro', e.target.value)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Persona */}
      <section className="card">
        <h3 className="card-header">אישיות ה-AI</h3>

        <div className="mb-3 flex items-start gap-2 rounded-lg bg-blue-50 p-3 text-xs text-blue-900">
          <Info size={15} aria-hidden="true" className="mt-0.5 flex-shrink-0" />
          <div>
            <p>
              זהו הטקסט שמגדיר איך ה-AI מתנהג באזור הזה — מה מותר לו להסביר, מה אסור לו
              להמליץ, ובאיזה טון. הוא נשלח בכל שיחה.
            </p>
            <p className="mt-1">
              <strong>שמירה מאפסת את מטמון ה-Prompt</strong> — ההודעה הראשונה אחרי השינוי
              תעלה מעט יותר, ואז המחיר חוזר לעצמו. הוספת <em>כללים</em> (בלשונית "כללים
              ל-AI") לא מאפסת את המטמון, ולכן לשינויים קטנים עדיף להוסיף כלל מאשר לערוך כאן.
            </p>
            <p className="mt-1">
              אורך נוכחי: כ-{personaTokens} טוקנים.{' '}
              {personaTokens < minTokens
                ? `מתחת ל-${minTokens} טוקנים המטמון לא פועל כלל וכל הודעה תעלה מלא.`
                : 'מעל הסף שנדרש כדי שהמטמון יפעל.'}
            </p>
          </div>
        </div>

        <textarea
          aria-label="אישיות ה-AI"
          dir="rtl"
          className="input min-h-[420px] font-mono text-xs leading-relaxed"
          value={draft.personaPrompt || ''}
          onChange={(e) => set('personaPrompt', e.target.value)}
        />

        <button type="button" onClick={handleRestore} className="btn btn-secondary mt-3">
          <RotateCcw size={15} aria-hidden="true" className="ms-1" />
          שחזור אישיות ברירת המחדל
        </button>
      </section>

      {/* Cost — owner-only, same reason as the switches above. */}
      {isOwner && (
      <section className="card">
        <h3 className="card-header">עלות</h3>
        <label htmlFor="track-cap" className="mb-1 block text-sm font-medium text-gray-700">
          מכסת AI לכל משתמש באזור זה (דולר)
        </label>
        <input
          id="track-cap"
          type="number"
          step="0.1"
          min="0"
          dir="ltr"
          className="input max-w-[12rem]"
          value={draft.spendCapUSD ?? 1}
          onChange={(e) => set('spendCapUSD', Number(e.target.value))}
        />
        <p className="mt-2 text-xs text-gray-600">
          המכסה נספרת בנפרד לכל אזור, כך ששימוש באזור אחד לא אוכל את המכסה של אחר. כשהמכסה
          נגמרת הצ'אט נחסם — אבל התוכן הלימודי נשאר פתוח לגמרי.
        </p>
      </section>
      )}

      <div className="flex items-center gap-3">
        <button type="button" onClick={handleSave} disabled={saving} className="btn btn-primary">
          <Save size={16} aria-hidden="true" className="ms-1" />
          {saving ? 'שומר...' : 'שמירת הגדרות'}
        </button>
        <span className="text-xs text-gray-500">
          {record.activeTopicCount ?? 0} נושאים פעילים מתוך {record.topicCount ?? 0}
        </span>
      </div>
    </div>
  )
}
